
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ExperienceHero from './components/ExperienceHero';
import ExperienceFilters from './components/ExperienceFilters';
import ExperienceCard from './components/ExperienceCard';

export default function ExperiencesPage() {
  useEffect(() => {
    // Update page title and meta description for experiences page
    document.title = 'Alabama Eco-Luxury Glamping Experiences - Forest, Mountain & Desert | OED Glamping';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover unique eco-luxury glamping experiences in Alabama. From forest retreats to mountain adventures and desert escapes. Book your sustainable outdoor hospitality experience with OED Glamping today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Alabama eco-luxury glamping experiences, forest retreats, mountain adventures, desert escapes, sustainable outdoor hospitality');
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://oedglampire.com/experiences');

    // Update social media images
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://oedglampire.com/experiences');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Alabama Eco-Luxury Glamping Experiences | OED Glamping');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Discover premium eco-luxury glamping experiences in Alabama\'s most beautiful natural settings. Mountain retreats, forest escapes, lakeside adventures, and desert sanctuaries.');
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', 'https://public.readdy.ai/ai/img_res/3293f2aa79c3467bc0e9df7c99fef4dd.jpg');
    }

    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', 'https://oedglampire.com/experiences');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Alabama Eco-Luxury Glamping Experiences | OED Glamping');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'Discover premium eco-luxury glamping experiences in Alabama\'s most beautiful natural settings. Mountain retreats, forest escapes, lakeside adventures.');
    }

    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', 'https://readdy.ai/api/search-image?query=Collage%20showcasing%20diverse%20eco-luxury%20glamping%20experiences%20across%20Alabama%20including%20mountain%20forest%20lakeside%20and%20desert%20settings%20with%20luxury%20tents%20domes%20and%20cabins%20in%20beautiful%20natural%20landscapes%20professional%20tourism%20photography%20warm%20inviting%20lighting%20showcasing%20outdoor%20adventure%20and%20sustainable%20luxury%20accommodations&width=1200&height=630&seq=experiences-social&orientation=landscape');
    }

    // Add TouristAttraction Schema.org JSON-LD
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript && existingScript.textContent?.includes('TouristAttraction')) {
      document.head.removeChild(existingScript);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": "OED Glamping Experiences",
      "description": "Premium eco-luxury glamping experiences in Alabama's most beautiful natural settings",
      "url": "https://oedglampire.com/experiences",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Talladega",
        "addressRegion": "AL",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 33.4359,
        "longitude": -86.1058
      },
      "provider": {
        "@type": "Organization",
        "name": "OED Glamping Retreat Developments",
        "url": "https://oedglampire.com"
      }
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove && scriptToRemove.textContent?.includes('TouristAttraction')) {
        document.head.removeChild(scriptToRemove);
      }
    };
  }, []);

  const experiences = [
    {
      id: 1,
      title: "Forest Retreat Experience",
      location: "Alabama Woodlands",
      price: "$299/night",
      rating: 4.9,
      image: "https://readdy.ai/api/search-image?query=Luxury%20glamping%20dome%20nestled%20in%20dense%20Alabama%20forest%20with%20towering%20pine%20and%20oak%20trees%2C%20eco-friendly%20sustainable%20accommodation%20surrounded%20by%20lush%20green%20woodland%2C%20filtered%20sunlight%20through%20canopy%2C%20serene%20forest%20atmosphere%20with%20premium%20outdoor%20hospitality%20design&width=400&height=300&seq=forest-exp-1&orientation=landscape",
      features: ["Forest Immersion", "Wildlife Viewing", "Hiking Trails"],
      activities: ["Forest Immersion", "Wildlife Viewing", "Hiking Trails", "Nature Photography", "Bird Watching"],
      duration: "2-3 days",
      difficulty: "Easy",
      slug: "forest",
      description: "Immerse yourself in the tranquil beauty of Alabama's pristine forests with luxury eco-accommodations."
    },
    {
      id: 2,
      title: "Mountain Adventure Experience",
      location: "Appalachian Foothills",
      price: "$349/night",
      rating: 4.8,
      image: "https://readdy.ai/api/search-image?query=Premium%20glamping%20tent%20on%20Alabama%20mountain%20ridge%20with%20panoramic%20views%20of%20Appalachian%20foothills%2C%20luxury%20eco-friendly%20accommodation%20with%20dramatic%20landscape%20backdrop%2C%20warm%20sunrise%20lighting%20over%20rolling%20mountains%2C%20adventure%20tourism%20photography&width=400&height=300&seq=mountain-exp-1&orientation=landscape",
      features: ["Mountain Views", "Rock Climbing", "Stargazing"],
      activities: ["Mountain Views", "Rock Climbing", "Stargazing", "Hiking", "Photography", "Sunrise Viewing"],
      duration: "2-4 days",
      difficulty: "Moderate",
      slug: "mountain",
      description: "Experience breathtaking mountain vistas and outdoor adventures in Alabama's Appalachian foothills."
    },
    {
      id: 3,
      title: "Lakeside Serenity Experience",
      location: "Alabama Lake District",
      price: "$279/night",
      rating: 4.9,
      image: "https://readdy.ai/api/search-image?query=Elegant%20glamping%20accommodation%20beside%20pristine%20Alabama%20lake%20with%20crystal%20clear%20waters%20reflecting%20surrounding%20forest%2C%20luxury%20eco-friendly%20structure%20with%20private%20deck%20overlooking%20water%2C%20peaceful%20sunset%20lighting%2C%20premium%20lakeside%20hospitality%20experience&width=400&height=300&seq=lakeside-exp-1&orientation=landscape",
      features: ["Lakefront Access", "Water Sports", "Fishing"],
      activities: ["Lakefront Access", "Water Sports", "Fishing", "Kayaking", "Swimming", "Sunset Viewing"],
      duration: "1-3 days",
      difficulty: "Easy",
      slug: "lakeside",
      description: "Relax by pristine Alabama lakes with water activities and peaceful natural surroundings."
    },
    {
      id: 4,
      title: "Desert Escape Experience",
      location: "Alabama Badlands",
      price: "$329/night",
      rating: 4.7,
      image: "https://readdy.ai/api/search-image?query=Modern%20glamping%20structure%20in%20unique%20Alabama%20desert-like%20landscape%20with%20red%20rock%20formations%20and%20sparse%20vegetation%2C%20luxury%20eco-friendly%20accommodation%20under%20vast%20open%20sky%2C%20dramatic%20sunset%20lighting%2C%20distinctive%20geological%20features&width=400&height=300&seq=desert-exp-1&orientation=landscape",
      features: ["Unique Landscape", "Stargazing", "Photography"],
      activities: ["Unique Landscape", "Stargazing", "Photography", "Desert Hiking", "Geology Tours"],
      duration: "2-3 days",
      difficulty: "Moderate",
      slug: "desert",
      description: "Discover Alabama's unique desert-like landscapes with luxury accommodations under starlit skies."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navigation />
        <main>
          <header>
            <h1 className="sr-only">Alabama Eco-Luxury Glamping Experiences</h1>
            <ExperienceHero />
          </header>
          <section aria-label="Experience Filters">
            <ExperienceFilters />
          </section>
          <section aria-label="Available Experiences" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  <strong>Discover Our Unique Experiences</strong>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Choose from our curated selection of <strong>eco-luxury glamping experiences</strong> designed to connect you with Alabama's natural beauty.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {experiences.map((experience) => (
                  <ExperienceCard key={experience.id} experience={experience} />
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
